import { Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'

import Reveal from '@/components/motion/Reveal'
import { cn } from '@/lib/utils'

export type SubscriptionStep = 'pick' | 'pay' | 'done'

const STEPS: readonly { id: SubscriptionStep; label: string }[] = [
  { id: 'pick', label: 'Pick a plan' },
  { id: 'pay', label: 'Pay' },
  { id: 'done', label: 'Done' },
]

interface SubscriptionStepsProps {
  current: SubscriptionStep
  className?: string
}

const CHIP = 'flex min-h-8 min-w-0 items-center gap-2 py-1 pr-3 pl-2'

/**
 * Pick a plan → Pay → Done, the rail the subscription flow's pages share (mock `su-steps`,
 * COMPARE-ac §8–9): 32-tall chips at their own width, a round 20 number disc and a 14 label.
 * The lit step is gold-soft with a gold disc that scales in and a gold 600 label; a step before it
 * is done (raised fill, ✓ on the default edge grey, 600 secondary label); the rest wait on the
 * inset grey in the tertiary ink.
 *
 * A done "Pick a plan" is a link back to the plans (mock `su-step__link`): hover lays the inset
 * grey over it and turns the label white. "Pay" is never a link once done: the payment it stands
 * for has already gone through.
 */
export default function SubscriptionSteps({ current, className }: SubscriptionStepsProps) {
  const currentIndex = STEPS.findIndex((step) => step.id === current)

  return (
    <ol
      aria-label="Subscription steps"
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {STEPS.map((step, index) => {
        const done = index < currentIndex
        const active = index === currentIndex
        const disc = (
          <span
            className={cn(
              'inline-flex size-5 shrink-0 items-center justify-center rounded-full text-h-xs',
              active
                ? 'bg-accent text-accent-foreground'
                : done
                  ? 'bg-border text-foreground'
                  : 'bg-popover text-muted-foreground-subtle',
            )}
            aria-hidden="true"
          >
            {done ? <Check className="size-3" /> : index + 1}
          </span>
        )
        const body = (
          <>
            {active ? (
              <Reveal as="span" variant="scale-in" className="inline-flex shrink-0">
                {disc}
              </Reveal>
            ) : (
              disc
            )}
            <span
              className={cn(
                'truncate',
                active
                  ? 'text-h-s text-accent'
                  : done
                    ? 'text-h-s text-muted-foreground transition-colors duration-fast group-hover/step:text-foreground group-focus-visible/step:text-foreground'
                    : 'text-body-m text-muted-foreground-subtle',
              )}
            >
              {step.label}
            </span>
            {done && <span className="sr-only">, done</span>}
          </>
        )
        return (
          <li
            key={step.id}
            aria-current={active ? 'step' : undefined}
            className={cn(
              'flex min-w-0',
              active ? 'bg-tone-accent-soft' : done ? 'bg-popover' : 'bg-muted',
            )}
          >
            {done && step.id === 'pick' ? (
              <Link
                to="/subscription"
                className={cn(
                  CHIP,
                  'group/step flex-1 transition-colors duration-fast hover:bg-muted focus-visible:bg-muted',
                )}
              >
                {body}
              </Link>
            ) : (
              <span className={CHIP}>{body}</span>
            )}
          </li>
        )
      })}
    </ol>
  )
}
